import { LoginUser, LocalUser, IdentityProvider } from '../models/user';
import { deleteLocalUser, getLocalUser, setLocalUser } from './user';
import { AuthApi } from '../generated/apis/AuthApi';
import { apiBasePath } from '../generated/clients';
import { Configuration } from '../generated';

const authApi = new AuthApi(new Configuration({ basePath: apiBasePath }));

export async function login(user: LoginUser): Promise<boolean> {
  try {
    const resp = (await authApi.authLoginRaw({ userCredentials: { username: user.email, password: user.password } })).raw;
    if (resp.ok) {
      console.log('User successfully logged in');
      const respJson = await resp.json();
      setLocalUser({
        ...user,
        currentIdentityProvider: IdentityProvider.email,
        lastLogin: respJson.lastLogin,
        accountType: respJson.accountType,
        email: respJson.email,
        username: respJson.username,
      });
      const token = respJson.access_token;
      setToken(token);

      return true;
    } else {
      console.warn('Login failed', resp.status, resp.statusText);
      return false;
    }
  } catch (e) {
    console.warn('Login failed', e);
    return false;
  }
}

export async function loginWithGoogleCode(code: string): Promise<boolean> {
  const resp = (await authApi.authLoginWithGoogleCodeRaw({ code: encodeURI(code), clientType: 'web' })).raw;

  if (resp.ok) {
    console.log('User successfully logged in');
    const respJson = (await resp.json()) as LocalUser;
    setLocalUser({
      identityProviderToken: respJson.identityProviderToken,
      currentIdentityProvider: IdentityProvider.google,
      accountType: respJson.accountType,
      password: '',
      email: respJson.email,
      lastLogin: respJson.lastLogin,
    });
    const token = respJson.access_token;
    if (token) setToken(token);

    return true;
  } else {
    console.warn('Login failed');
    return false;
  }
}

export async function loginWithAppleCode(code: string): Promise<boolean> {
  const resp = (await authApi.authLoginWithAppleCodeRaw({ code: encodeURI(code), clientType: 'web' })).raw;

  if (resp.ok) {
    console.log('User successfully logged in');
    const respJson = (await resp.json()) as LocalUser;
    setLocalUser({
      identityProviderToken: respJson.identityProviderToken,
      currentIdentityProvider: IdentityProvider.apple,
      accountType: respJson.accountType,
      password: '',
      email: respJson.email,
      lastLogin: respJson.lastLogin,
    });

    const token = respJson.access_token;
    if (token) setToken(token);

    return true;
  } else {
    console.warn('Login failed');
    return false;
  }
}

//TODO: This is problematic if used in parallel, Promise.all etc.
//Better to update session jwt in this case. (separate method)
export async function fetchWithReauth(input: RequestInfo, init?: RequestInit | undefined, force?: boolean) {
  const localUser = getLocalUser();
  //TODO: Differentiate login types
  let result = await fetch(input, {
    ...init,
    headers: { ...init?.headers, ...{ Authorization: `Bearer ${getToken()}` } },
  });
  if ((result.status === 401 || force) && localUser) {
    console.log('Session is probably expired. Trying to login with stored credentials.');
    if (localUser.currentIdentityProvider === IdentityProvider.email) {
      const reloginResult = await login({ ...localUser, email: localUser.email ?? localUser.username });
      if (!reloginResult) {
        await logout();
        window.location.href = '/';
      }
    } else if (localUser.currentIdentityProvider === IdentityProvider.google || localUser.currentIdentityProvider === IdentityProvider.apple) {
      //TODO: Fix weird double quote problem
      const resp = await authApi.authRefreshJwtForExternalProvider({ refreshJWT: { jwtString: (localStorage.getItem('token') ?? '').replace(/^"|"$/g, ''), clientType: 'web' } });
      if (resp) {
        setToken(resp.accessToken);
      } else {
        await logout();
        window.location.href = '/';
      }
    }
    return await fetch(input, {
      ...init,
      headers: {
        ...init?.headers,
        ...{ Authorization: `Bearer ${getToken()}` },
      },
    });
  } else return result;
}

const setToken = (token: string) => {
  localStorage.setItem('token', JSON.stringify(token));
};

const getToken = () => {
  if (localStorage.getItem('token')) return JSON.parse(localStorage.getItem('token') || '');
  else return '';
};

export const logout = async () => {
  localStorage.removeItem('token');
  localStorage.removeItem('isLoggedIn');
  deleteLocalUser();
};
